import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, Factory, FlaskConical, IndianRupee, MessageCircle, Phone, Wrench } from "lucide-react";
import usePageMeta, { useJsonLd } from "@/hooks/usePageMeta";
import useProducts from "@/hooks/useProducts";
import { useInquiry } from "@/context/InquiryContext";
import HeroSlider from "@/components/HeroSlider";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { Counter, FadeUp, SectionHeading } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { FACILITY } from "@/lib/images";

const CHAPTERS = [
  {
    n: "01",
    title: "Manufacture & Supply",
    copy: "Laboratory, research, educational and medical instruments manufactured and supplied under the 'Krithish' brand — from autoclaves and ovens to furnaces, baths and clean-air systems.",
  },
  {
    n: "02",
    title: "Quality You Can Check",
    copy: "Every unit passes in-house quality checks and calibration before dispatch. We have never compromised on the quality and the service provided to the customer.",
  },
  {
    n: "03",
    title: "Repair & Servicing",
    copy: "All scientific, industrial and lab equipment repairing & servicing undertaken — electrical, electronic and cooling equipment — by our own experienced team.",
  },
  {
    n: "04",
    title: "Chemicals & Glass Wares",
    copy: "A complete range of laboratory glassware and chemicals supplied alongside equipment, so you order everything from one trusted source.",
  },
];

const WHY = [
  { icon: Factory, title: "Made by Krithish", copy: "Instruments manufactured at our Chennai workshop — direct from the maker, no middlemen." },
  { icon: Wrench, title: "Repair & Servicing", copy: "All scientific, industrial & lab equipment — electrical, electronic & cooling — serviced in-house." },
  { icon: FlaskConical, title: "Glass Wares & Chemicals", copy: "All types of lab glassware and chemicals supplied along with your equipment order." },
  { icon: ClipboardCheck, title: "Customer Satisfaction", copy: "An excellent track record for the best customer satisfaction — we keep in constant touch." },
];

const STATS = [
  { to: 15, suffix: "+", label: "Years of Experience" },
  { to: 40, suffix: "+", label: "Product Types" },
  { to: 8, suffix: "", label: "Core Equipment Lines" },
];

export default function Home() {
  usePageMeta(
    "Krithish Scientific Company | Scientific, Industrial & Lab Equipments Manufacturer, Chennai",
    "Krithish Scientific Company, Chennai — manufacturer & leading supplier of laboratory, research, educational and medical instruments for 15+ years. Autoclaves, BOD incubators, ovens, water baths, furnaces, laminar air flow. Repairing & servicing undertaken."
  );

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Krithish Scientific Company",
    email: SITE.email,
    telephone: SITE.phoneDisplay,
    address: {
      "@type": "PostalAddress",
      streetAddress: "No. 15, Pugazhendhi Street Extension, Sanjay Gandhi Colony, Choolai Pallam, M.G.R. Nagar",
      addressLocality: "Chennai",
      postalCode: "600078",
      addressRegion: "Tamil Nadu",
      addressCountry: "IN",
    },
    taxID: "33AINPR0146B1ZM",
  });

  const { data: products } = useProducts();
  const { openOrder } = useInquiry();
  const featured = (products || []).filter((p) => p.featured).slice(0, 4);

  return (
    <>
      <HeroSlider />
      <Marquee />

      {/* Welcome / Manifesto */}
      <section className="relative bg-white py-24 lg:py-32" data-testid="welcome-section">
        <div className="absolute inset-0 bg-grid-light opacity-60" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-32">
                <FadeUp>
                  <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-safety">Who We Are</p>
                  <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                    Welcome to Krithish Scientific Company
                  </h2>
                  <p className="mt-6 text-base leading-relaxed text-slate-600">
                    Krithish Scientific Company is a Chennai-based manufacturer and leading supplier of{" "}
                    <span className="font-semibold text-slate-900">laboratory, research, educational and medical
                    instruments</span> for over 15 years. From autoclaves, ovens and incubators to furnaces, baths and
                    clean-air systems, every unit carries the trusted <span className="font-semibold text-slate-900">"Krithish"</span> name.
                  </p>
                  <p className="mt-4 text-base leading-relaxed text-slate-600">
                    We also supply <span className="font-semibold text-slate-900">chemicals &amp; glass wares</span>, and
                    undertake <span className="font-semibold text-slate-900">repairing &amp; servicing</span> of all
                    scientific, industrial and lab equipment — electrical, electronic &amp; cooling — so your lab keeps
                    running long after the sale.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {["GSTIN: 33AINPR0146B1ZM", "Est. 2006", "Chennai, India"].map((b) => (
                      <span key={b} className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600">
                        {b}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/about"
                    className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-safety transition hover:text-safety-dark"
                  >
                    More about us <ArrowRight size={16} />
                  </Link>
                </FadeUp>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="space-y-2">
                {CHAPTERS.map((c, i) => (
                  <FadeUp key={c.n} delay={i * 0.06}>
                    <div className="group flex gap-6 border-b border-slate-200 py-8 transition hover:border-rose-200 sm:gap-10">
                      <span className="text-outline font-display text-5xl font-extrabold leading-none transition group-hover:[-webkit-text-stroke-color:#D01A46] sm:text-6xl">
                        {c.n}
                      </span>
                      <div>
                        <h3 className="font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{c.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">{c.copy}</p>
                      </div>
                    </div>
                  </FadeUp>
                ))}
              </div>

              <div className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-4" data-testid="stats-band">
                {STATS.map((s, i) => (
                  <FadeUp key={s.label} delay={i * 0.06}>
                    <p className="font-display text-3xl font-extrabold tracking-tight text-safety sm:text-4xl">
                      <Counter to={s.to} suffix={s.suffix} />
                    </p>
                    <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
                  </FadeUp>
                ))}
                <FadeUp delay={0.18}>
                  <div className="flex h-full items-center rounded-xl border border-rose-100 bg-rose-50/60 px-4 py-3">
                    <p className="text-xs font-semibold leading-snug text-slate-800">
                      Service on electrical, electronic &amp; cooling equipment
                    </p>
                  </div>
                </FadeUp>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us — bento */}
      <section className="bg-slate-50 py-24 lg:py-32" data-testid="why-choose-us">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            eyebrow="Why Choose Us"
            title="One maker. One service team. One call."
            sub="What laboratories, hospitals, schools and industries get when they buy from Krithish — and why they come back."
          />
          <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
            <FadeUp className="md:col-span-8 md:row-span-2">
              <div className="relative h-full min-h-[420px] overflow-hidden rounded-2xl">
                <img src={FACILITY.cleanroom} alt="Equipment manufacturing at the Krithish workshop" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/35 to-transparent" />
                <div className="relative flex h-full flex-col justify-end p-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-rose-300">All Scientific, Industrial & Lab Equipments</p>
                  <h3 className="mt-3 max-w-lg font-display text-2xl font-bold leading-snug text-white sm:text-3xl">
                    Manufacturing, supply, repairing &amp; servicing — all undertaken under one roof.
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-white/70">
                    From a single water bath to a full lab setup, deal directly with the people who build and service
                    your equipment.
                  </p>
                </div>
              </div>
            </FadeUp>
            {WHY.map((w, i) => (
              <FadeUp key={w.title} delay={0.06 * (i + 1)} className="md:col-span-4">
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-rose-200 hover:shadow-lg hover:shadow-slate-200/50">
                  <span className="inline-flex rounded-lg bg-rose-50 p-2.5 text-safety">
                    <w.icon size={20} />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.copy}</p>
                </div>
              </FadeUp>
            ))}
            <FadeUp delay={0.2} className="md:col-span-6">
              <div className="flex h-full items-center justify-between gap-6 rounded-2xl border border-rose-200 bg-rose-50/60 p-6">
                <div>
                  <h3 className="font-display text-lg font-bold tracking-tight text-slate-900">Competent price, honest advice</h3>
                  <p className="mt-1.5 text-sm text-slate-600">Products at a very competent price — with staff who guide you to the right unit.</p>
                </div>
                <IndianRupee size={44} className="shrink-0 text-safety" />
              </div>
            </FadeUp>
            <FadeUp delay={0.26} className="md:col-span-6">
              <div className="flex h-full items-center justify-between gap-6 rounded-2xl border border-slate-900 bg-navy p-6">
                <div>
                  <h3 className="font-display text-lg font-bold tracking-tight text-white">Talk to Anbazhagan</h3>
                  <p className="mt-1.5 text-sm text-white/60">{SITE.hours} — we answer with specs, not scripts.</p>
                </div>
                <a href={SITE.phoneHref} data-testid="why-choose-phone" className="shrink-0 rounded-lg bg-safety px-4 py-2.5 font-mono text-xs font-semibold text-white transition hover:bg-safety-dark">
                  {SITE.phoneDisplay}
                </a>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-white py-24 lg:py-32" data-testid="featured-products">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading
              eyebrow="Featured Products"
              title="Built for the demanding lab."
              sub="Four of our most-ordered lines — each with full specifications and online ordering."
            />
            <FadeUp delay={0.1}>
              <Link to="/products" data-testid="view-all-products" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-800 transition hover:border-safety hover:text-safety">
                View all products <ArrowRight size={16} />
              </Link>
            </FadeUp>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p, i) => (
              <FadeUp key={p.slug} delay={i * 0.06} className="h-full">
                <ProductCard p={p} index={i} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden bg-navy py-20 lg:py-24" data-testid="cta-band">
        <div className="absolute inset-0 bg-grid-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <FadeUp>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-rose-300">Custom & Bulk Orders</p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
              Need a custom-built unit?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-white/60">
              Send your specification — our team responds within one working day.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button data-testid="cta-order-btn" onClick={() => openOrder()} className="inline-flex items-center gap-2 rounded-lg bg-safety px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-safety-dark">
                Place an Order Enquiry <ArrowRight size={16} />
              </button>
              <a href={SITE.phoneHref} className="inline-flex items-center gap-2 rounded-lg border border-white/40 px-6 py-3.5 font-mono text-sm font-semibold text-white transition hover:bg-white hover:text-navy">
                <Phone size={15} /> {SITE.phoneDisplay}
              </a>
              <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-white/40 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-white hover:text-navy">
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
